import React, {useEffect, useMemo} from "react"
import {SchoolClassCourseModule, User} from "models"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import AppHistory from "shared/history"
import {digg} from "diggerize"
import EventEmitter from "events"
import findOrCreateTeamSurveyStep from "components/survey-steps/find-or-create-team-survey-step"
import Logger from "@kaspernj/api-maker/build/logger"
import memo from "set-state-compare/build/memo"
import Routes from "shared/routes"
import setLayout from "shared/set-layout"
import SurveysBottomBar from "components/surveys/bottom-bar"
import SurveysPageContainer from "components/surveys/page-container"
import SurveyStepsPresentation from "components/survey-steps/presentation"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import {useFrontLayout} from "components/front-layout"
import useHiddenTawkWidget from "shared/use-hidden-tawk-widget"
import useModel from "@kaspernj/api-maker/build/use-model"
import {useParams} from "@kaspernj/api-maker/build/router/route"
import {View} from "react-native"

export default memo(shapeComponent(class RoutesUserSchoolClassCourseModules extends ShapeComponent {
  teamSurveyStepLoadRequestId = 0

  componentDidUpdate(prevProps, prevState) {
    const prevSurveyStepId = prevState?.teamSurveyStep?.surveyStep()?.id()
    const nextSurveyStepId = this.s.teamSurveyStep?.surveyStep()?.id()
    const prevTeamSurveyStepId = prevState?.teamSurveyStep?.id()
    const nextTeamSurveyStepId = this.s.teamSurveyStep?.id()

    if (prevSurveyStepId != nextSurveyStepId || prevTeamSurveyStepId != nextTeamSurveyStepId) {
      this.updateLayout()
    }
  }

  setup() {
    setLayout("user")

    this.logger ||= new Logger({name: "RoutesUserSchoolClassCourseModules"})
    this.logger.setDebug(false)

    this.useStates({
      teamSurveyStep: undefined
    })

    const currentUser = useCurrentUser()
    const frontLayout = useFrontLayout()
    const params = useParams()
    const {schoolClassCourseModule, schoolClassCourseModuleId} = useModel(
      SchoolClassCourseModule,
      {
        match: {params},
        preload: [
          "school_class_course.course.brain_break",
          "school_class_course.course.challenge",
          "school_class_course.course.course_groups",
          "school_class_course.school_class",
          "course_module",
          "team_survey.survey"
        ],
        select: {
          Course: ["backgroundImageUrl", "courseModulesCount", "id"],
          CourseGroup: ["identifier"],
          CourseModule: ["position"],
          SchoolClass: ["id", "schoolId"],
          SchoolClassCourse: ["courseId", "id", "schoolClassId", "state"],
          SchoolClassCourseModule: ["id", "state"],
          Survey: ["surveyStepsCount"],
          TeamSurvey: ["id", "preview", "state"]
        }
      }
    )
    const schoolClassCourse = schoolClassCourseModule?.schoolClassCourse()
    const course = schoolClassCourse?.course()
    const courseModule = schoolClassCourseModule?.courseModule()
    const surveyStep = this.s.teamSurveyStep?.surveyStep()
    const survey = schoolClassCourseModule?.teamSurvey()?.survey()
    const teamSurvey = schoolClassCourseModule?.teamSurvey()
    const teamSurveyStepId = digg(params, "team_survey_step_id")

    this.setInstance({
      course,
      courseModule,
      currentUser,
      frontLayout,
      schoolClassCourse,
      schoolClassCourseModule,
      schoolClassCourseModuleId,
      survey,
      surveyStep,
      surveyStepsPresentationEvents: useMemo(() => new EventEmitter(), []),
      teamSurvey,
      teamSurveyStepId
    })

    useMemo(() => {
      if (currentUser && schoolClassCourseModule) {
        this.loadTeamSurveyStep()
      } else {
        this.teamSurveyStepLoadRequestId += 1
        this.setState({teamSurveyStep: undefined})
      }
    }, [currentUser?.id(), schoolClassCourseModule, teamSurveyStepId])

    useHiddenTawkWidget()

    useEffect(() => {
      this.updateLayout()
    }, [
      course?.id(),
      frontLayout,
      schoolClassCourse?.id(),
      schoolClassCourseModule?.id(),
      this.s.teamSurveyStep?.id(),
      surveyStep?.id()
    ])
  }

  loadTeamSurveyStep = async () => {
    const requestId = this.teamSurveyStepLoadRequestId + 1

    this.teamSurveyStepLoadRequestId = requestId

    try {
      const teamSurveyStep = await findOrCreateTeamSurveyStep({
        teamSurveyId: this.tt.schoolClassCourseModule.teamSurvey().id(),
        teamSurveyStepId: this.tt.teamSurveyStepId,
        withQuery: ({query}) => query.ransack({team_survey_school_class_course_module_id_eq: this.tt.schoolClassCourseModuleId})
      })

      if (!this.isMounted() || requestId != this.teamSurveyStepLoadRequestId) return

      this.setState({teamSurveyStep}, () => {
        this.updateLayout()
      })
    } catch (error) {
      if (!this.isMounted() || requestId != this.teamSurveyStepLoadRequestId) {
        this.logger.error([
          "Ignoring stale teamSurveyStep load error after route change",
          error,
          {
            currentRequestId: this.teamSurveyStepLoadRequestId,
            requestId,
            schoolClassCourseModuleId: this.tt.schoolClassCourseModuleId,
            teamSurveyStepId: this.tt.teamSurveyStepId
          }
        ])

        return
      }

      throw error
    }
  }

  render() {
    const {schoolClassCourseModule, surveyStep, surveyStepsPresentationEvents, teamSurvey} = this.tt
    const {teamSurveyStep} = this.s

    return (
      <View
        dataSet={this.cache("rootViewDataSet", {
          route: "user/school-class-course-modules/show",
          surveyStepId: surveyStep?.id(),
          teamSurveyStepId: teamSurveyStep?.id()
        }, [surveyStep?.id(), teamSurveyStep?.id()])}
      >
        {schoolClassCourseModule && surveyStep && this.s.teamSurveyStep &&
          <div className="content-container">
            <SurveysPageContainer>
              <SurveyStepsPresentation
                events={surveyStepsPresentationEvents}
                key={`survey-steps-presentation-${schoolClassCourseModule.id()}-${surveyStep.id()}`}
                onAnswersSubmitted={this.tt.onAnswersSubmitted}
                schoolClass={schoolClassCourseModule.schoolClassCourse().schoolClass()}
                submitButton={false}
                surveyStep={surveyStep}
                teamSurvey={teamSurvey}
                teamSurveyStep={this.s.teamSurveyStep}
              />
            </SurveysPageContainer>
          </div>
        }
      </View>
    )
  }

  bottomBar() {
    const {schoolClassCourseModule} = this.tt
    const surveyStepFromTeam = this.s.teamSurveyStep?.surveyStep()

    return (
      <>
        {schoolClassCourseModule && surveyStepFromTeam && this.s.teamSurveyStep &&
          <SurveysBottomBar
            color="white"
            onCancelClicked={this.tt.onCancelClicked}
            onEndClicked={this.tt.onEndClicked}
            onNextStepClicked={this.tt.onNextStepClicked}
            onPreviousStepClicked={this.tt.onPreviousStepClicked}
            survey={schoolClassCourseModule.teamSurvey().survey()}
            surveyStep={surveyStepFromTeam}
            teamSurvey={schoolClassCourseModule.teamSurvey()}
            teamSurveyStep={this.s.teamSurveyStep}
          />
        }
      </>
    )
  }

  updateLayout() {
    const {course, frontLayout, schoolClassCourse} = this.tt
    const surveyStepFromTeam = this.s.teamSurveyStep?.surveyStep()

    frontLayout?.setState({
      background: course?.backgroundImageUrl(),
      bottomBar: this.bottomBar(),
      contentCentered: true,
      darkBackground: true,
      headTitle: surveyStepFromTeam?.name(),
      logo: false,
      menu: false,
      schoolClass: schoolClassCourse?.schoolClass(),
      proTrialTopBar: false,
      userMenu: false,
      weekBar: false
    })
  }

  endModule = async () => {
    const {schoolClassCourse, schoolClassCourseModule, teamSurvey} = this.tt
    const promises = []

    if (this.isLastStep() && schoolClassCourseModule.state() != "completed" && !teamSurvey.preview()) {
      promises.push(
        User.stateEvent({model: schoolClassCourseModule, state_event: "complete"})
      )
    }

    if (this.isLastStep() && teamSurvey.state() != "completed") {
      promises.push(
        User.stateEvent({model: teamSurvey, state_event: "complete"})
      )
    }

    if (this.isLastModule() && schoolClassCourse.state() != "completed" && !teamSurvey.preview()) {
      promises.push(
        User.stateEvent({model: schoolClassCourse, state_event: "complete"})
      )
    }

    await Promise.all(promises)
    this.redirectBack()
  }

  redirectBack() {
    const {schoolClassCourse} = this.tt

    if (this.redirectToClassSpace()) {
      AppHistory.push(Routes.userSchoolClassClassSpacePath(schoolClassCourse.schoolClassId()))
    } else {
      AppHistory.push(Routes.userCoursePath(schoolClassCourse.courseId(), {school_class_course_id: schoolClassCourse.id()}))
    }
  }

  redirectToClassSpace() {
    const {course} = this.tt
    let redirectToClassSpace = false

    if (course.courseModulesCount() == 1) {
      const courseGroupIdentifiers = course.courseGroups().loaded().map((courseGroup) => courseGroup.identifier())

      if (course.brainBreak() || course.challenge() || courseGroupIdentifiers.includes("brain_break") || courseGroupIdentifiers.includes("challenge")) {
        redirectToClassSpace = true
      }
    }

    return redirectToClassSpace
  }

  isLastModule = () => {
    if (this.tt.courseModule.position() == this.tt.course.courseModulesCount()) return true
  }

  isLastStep = () => {
    if (this.s.teamSurveyStep.surveyStep().position() >= this.tt.survey.surveyStepsCount()) return true
  }

  onAnswersSubmitted = ({after, nextTeamSurveyStep}) => {
    if (after == "end") {
      this.endModule()
    } else if (after == "nextStep") {
      this.redirectToNextStep({nextTeamSurveyStep})
    } else if (after == "previousStep") {
      this.redirectToPreviousStep()
    } else {
      throw new Error(`Unhanded submit mode: ${after}`)
    }
  }

  onCancelClicked = () => this.redirectBack()
  onEndClicked = () => this.tt.surveyStepsPresentationEvents.emit("submit", {after: "end"})
  onNextStepClicked = () => this.tt.surveyStepsPresentationEvents.emit("submit", {after: "nextStep"})
  onPreviousStepClicked = () => this.tt.surveyStepsPresentationEvents.emit("submit", {after: "previousStep"})

  redirectToNextStep = ({nextTeamSurveyStep}) => {
    AppHistory.push(Routes.userSchoolClassCourseModuleStepPath(this.tt.schoolClassCourseModule.id(), nextTeamSurveyStep.id()))
  }

  redirectToPreviousStep = async () => {
    const previousStepResult = await this.s.teamSurveyStep.previousStep()
    const previousTeamSurveyStep = digg(previousStepResult, "previous_team_survey_step")

    AppHistory.push(Routes.userSchoolClassCourseModuleStepPath(this.tt.schoolClassCourseModule.id(), previousTeamSurveyStep.id()))
  }
}))
